import { queryOptions } from "@tanstack/react-query";

import {
  getCoachesByCity,
  getCoachesByCategory,
  getCoachesBySubcategory,
  getCoachesByCategoryAndCity,
  getCoachesBySubcategoryAndCity,
  getFoundingCoaches,
} from "~/lib/functions/coach-grouping";
import { MembershipPlan } from "~/lib/schemas/db-enums";
import {
  getCoachData,
  getOtherOffers,
  getOfferByUsernameAndId,
} from "~/shared/loaders";

// Coach-related query options
export const coachQueryOptions = {
  // Public coach profile data
  byUsername: (username: string) =>
    queryOptions({
      queryKey: ["coach", "public", username],
      queryFn: () => getCoachData({ data: { username } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Public coach offers for Pro+ coaches. The server derives plan, main
  // offer, and visibility itself; membershipPlan here only skips the request
  // for coaches that can't have other offers.
  otherOffers: (coachId: string, membershipPlan?: MembershipPlan) =>
    queryOptions({
      queryKey: ["coach", "offers", coachId],
      queryFn: () => getOtherOffers({ data: { coachId } }),
      staleTime: 15 * 60 * 1000, // 15 minutes
      gcTime: 60 * 60 * 1000, // 1 hour
      enabled: membershipPlan === MembershipPlan.ProPlus,
    }),

  // Coaches filtered by city
  byCity: (citySlug: string) =>
    queryOptions({
      queryKey: ["coaches", "public", "city", citySlug],
      queryFn: () => getCoachesByCity({ data: { citySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Coaches filtered by category
  byCategory: (categorySlug: string) =>
    queryOptions({
      queryKey: ["coaches", "public", "category", categorySlug],
      queryFn: () => getCoachesByCategory({ data: { categorySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Coaches filtered by subcategory
  bySubcategory: (subcategorySlug: string) =>
    queryOptions({
      queryKey: ["coaches", "public", "subcategory", subcategorySlug],
      queryFn: () => getCoachesBySubcategory({ data: { subcategorySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Coaches filtered by category and city
  byCategoryAndCity: (categorySlug: string, citySlug: string) =>
    queryOptions({
      queryKey: [
        "coaches",
        "public",
        "category",
        categorySlug,
        "city",
        citySlug,
      ],
      queryFn: () =>
        getCoachesByCategoryAndCity({ data: { categorySlug, citySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  // Coaches filtered by subcategory and city
  bySubcategoryAndCity: (subcategorySlug: string, citySlug: string) =>
    queryOptions({
      queryKey: [
        "coaches",
        "public",
        "subcategory",
        subcategorySlug,
        "city",
        citySlug,
      ],
      queryFn: () =>
        getCoachesBySubcategoryAndCity({ data: { subcategorySlug, citySlug } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),

  founding: () =>
    queryOptions({
      queryKey: ["coaches", "public", "founding"],
      queryFn: () => getFoundingCoaches(),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    }),
} as const;

// Offer-related query options
export const offerQueryOptions = {
  // Public offer by username and offer ID
  byUsernameAndId: (username: string, offerId: string) =>
    queryOptions({
      queryKey: ["offer", username, offerId],
      queryFn: () => getOfferByUsernameAndId({ data: { username, offerId } }),
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      retry: (failureCount, error) => {
        // Don't retry on 404 errors
        if (error instanceof Response && error.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
    }),
} as const;
